import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  Stack,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  Skeleton,
  TablePagination,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import config from '../../Config';
import Swal from 'sweetalert2';

const formatDate = (isoString) => {
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };
  return new Date(isoString).toLocaleDateString(undefined, options);
};

const statusOptions = ['Pending', 'Confirmed', 'Completed', 'Cancelled'];

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);

  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.apiBaseUrl}/admin/getOrders`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (res.ok) setOrders((data.orders || []).reverse());
      else setError(data.message || 'Failed to fetch orders.');
    } catch {
      setError('Error fetching orders.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleStatusChange = async (orderId, newStatus) => {
    setUpdatingOrderId(orderId);

    Swal.fire({
      title: 'Updating Status...',
      text: 'Please wait',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.apiBaseUrl}/admin/changeOrderStatus`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ orderId, status: newStatus }),
      });

      const data = await res.json();

      if (res.ok) {
        setOrders((prev) =>
          prev.map((order) => (order.orderId === orderId ? { ...order, status: newStatus } : order))
        );

        Swal.fire({
          icon: 'success',
          title: 'Status Updated',
          text: `Order ${orderId} status changed to ${newStatus}`,
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Update Failed',
          text: data.message || 'Failed to update order status.',
        });
      }
    } catch {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Something went wrong while updating the status.',
      });
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const paginatedOrders = orders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <Typography variant={isSmDown ? 'h5' : 'h4'} fontWeight="bold" mb={3}>
        Orders
      </Typography>

      <Paper elevation={3} sx={{ p: 2, borderRadius: 3, overflowX: 'auto' }}>
        <Table size={isSmDown ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              <TableCell>Order ID</TableCell>
              <TableCell>Grand Total</TableCell>
              <TableCell>Order Date</TableCell>
              <TableCell>Reservation</TableCell>
              <TableCell>Reservation ID</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Products</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <TableRow key={idx}>
                  {Array.from({ length: 7 }).map((__, i) => (
                    <TableCell key={i}>
                      <Skeleton />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No data available
                </TableCell>
              </TableRow>
            ) : (
              paginatedOrders.map((order) => (
                <TableRow key={order.orderId} hover>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      color: 'primary.main',
                    }}
                    onClick={() => navigate(`/orders/${order.orderId}`)}
                  >
                    {order.orderId}
                  </TableCell>

                  <TableCell>£{order.grandTotal.toLocaleString('en-IN')}</TableCell>

                  <TableCell>{formatDate(order.createdAt)}</TableCell>

                  <TableCell sx={{ minWidth: 200 }}>
                    {order.reservation ? (
                      <Stack spacing={0.3}>
                        <Typography variant="body2" fontWeight="bold">
                          {order.reservation.guestName || '-'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {order.reservation.guestEmail || '-'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatDate(order.reservation.startDate)} -{' '}
                          {formatDate(order.reservation.endDate)}
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        None
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>{order.reservation?.reservationID || 'N/A'}</TableCell>

                  <TableCell>
                    <Select
                      value={order.status}
                      size="small"
                      disabled={updatingOrderId === order.orderId}
                      onChange={(e) => handleStatusChange(order.orderId, e.target.value)}
                      sx={{ minWidth: 120 }}
                    >
                      {statusOptions.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>

                  <TableCell>
                    <Stack spacing={1}>
                      {order.products.map((product, idx) => (
                        <Stack key={idx} direction="row" spacing={1} alignItems="center">
                          <Avatar
                            src={product.image}
                            alt={product.name}
                            variant="rounded"
                            sx={{ width: 56, height: 56 }}
                          />
                          <Box>
                            <Typography variant="body2" fontWeight="medium" noWrap maxWidth={200}>
                              {product.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                              Price: £{product.price.toLocaleString('en-IN')} x {product.quantity}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              display="block"
                              fontWeight="bold"
                            >
                              Total: £{product.total.toLocaleString('en-IN')}
                            </Typography>
                          </Box>
                        </Stack>
                      ))}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {orders.length > 0 && !loading && (
          <TablePagination
            component="div"
            count={orders.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[]}
          />
        )}
      </Paper>
    </Box>
  );
}
